import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Check,
  Clock as ClockIcon,
  X,
  Star,
  TrendingUp,
  Minus,
  Plus,
  CircleDot,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const fmtFt = (v) => `${Math.round(v || 0).toLocaleString("hu-HU")} Ft`;
const fmtMonth = (ym) => (ym ? `${ym.split("-")[0]}. ${ym.split("-")[1]}.` : "—");
const fmtIso = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}.`;
};

function statusMeta(key, t) {
  switch (key) {
    case "paid":
      return { label: t("finances.paid"), icon: Check, color: "emerald" };
    case "waiting_accountant":
      return { label: t("finances.waiting_accountant"), icon: ClockIcon, color: "amber" };
    case "waiting_payment":
      return { label: t("finances.waiting_payment"), icon: ClockIcon, color: "amber" };
    case "pending":
      return { label: t("finances.pending"), icon: ClockIcon, color: "amber" };
    case "draft":
      return { label: t("finances.draft"), icon: ClockIcon, color: "zinc" };
    case "approved":
      return { label: t("finances.approved"), icon: ClockIcon, color: "blue" };
    case "issued":
      return { label: t("finances.issued"), icon: Star, color: "zinc" };
    case "rejected":
      return { label: t("finances.rejected"), icon: X, color: "red" };
    default:
      return null;
  }
}

export default function PortalFinances() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/portal/finances");
        setData(data);
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || t("common.error"));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || !data) {
    return (
      <div className="space-y-4 px-4 pb-8 pt-6 md:px-6 md:pt-8">
        {["s1", "s2", "s3"].map((k) => (
          <div key={k} className="h-32 animate-pulse rounded-2xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900" />
        ))}
      </div>
    );
  }

  const tot = data.totals || {};
  const salaries = data.salaries || [];
  const bonuses = data.bonuses || [];
  const commissions = data.commissions || [];
  const deductions = data.deductions || [];
  return (
    <div className="space-y-6 px-4 pb-8 pt-6 md:px-6 md:pt-8">
      <header>
        <p className="text-[14px] text-zinc-500">{t("nav.finances")}</p>
        <h1 className="mt-1 text-[28px] font-semibold tracking-tight md:text-[32px]">
          {t("finances.title")}
        </h1>
      </header>

      {/* Big total hero */}
      <section className="rounded-3xl bg-white p-6 shadow-[0_1px_3px_0_rgba(0,0,0,0.05),0_2px_6px_-2px_rgba(0,0,0,0.04)] dark:bg-zinc-900 md:p-7">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 dark:bg-emerald-950/40">
          <TrendingUp className="h-3 w-3 text-emerald-600 dark:text-emerald-400" strokeWidth={2.2} />
          <span className="text-[11.5px] font-medium text-emerald-700 dark:text-emerald-300">
            {t("finances.salary_paid_total")}
          </span>
        </div>
        <p className="mt-3 font-mono text-[40px] font-semibold leading-none tabular-nums tracking-[-0.02em] md:text-[48px]">
          {fmtFt(tot.salary_paid)}
        </p>

        {tot.salary_pending > 0 && (
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1.5 dark:bg-amber-950/40">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            <span className="text-[12.5px] font-medium text-amber-800 dark:text-amber-300">
              {t("finances.salary_pending_with_amount", { amount: fmtFt(tot.salary_pending) })}
            </span>
          </div>
        )}

        <div className="mt-6 grid grid-cols-3 gap-3">
          <MiniCard label={t("finances.bonus")} value={fmtFt(tot.bonus_paid)} pending={tot.bonus_pending} icon={Plus} />
          <MiniCard label={t("finances.commission")} value={fmtFt(tot.commission_paid)} pending={tot.commission_pending} icon={Plus} />
          <MiniCard label={t("finances.deductions")} value={fmtFt(tot.deduction_total)} icon={Minus} negative />
        </div>
      </section>

      <Tabs defaultValue="salaries" className="space-y-4">
        <TabsList
          className="inline-flex h-auto w-full justify-start gap-1 overflow-x-auto rounded-full bg-zinc-100 p-1 dark:bg-zinc-800/60 md:w-auto"
        >
          {[
            { v: "salaries", n: t("finances.salary"), count: salaries.length },
            { v: "bonuses", n: t("finances.bonus"), count: bonuses.length },
            { v: "commissions", n: t("finances.commission"), count: commissions.length },
            { v: "deductions", n: t("finances.deduction"), count: deductions.length },
          ].map((tab) => (
            <TabsTrigger
              key={tab.v}
              value={tab.v}
              data-testid={`fin-tab-${tab.v}`}
              className="inline-flex h-9 items-center gap-1.5 rounded-full px-4 text-[13px] font-medium tracking-tight transition-colors data-[state=active]:bg-white data-[state=active]:text-zinc-900 data-[state=active]:shadow-[0_1px_3px_0_rgba(0,0,0,0.08)] dark:data-[state=active]:bg-zinc-700 dark:data-[state=active]:text-white"
            >
              {tab.n}
              <span className="font-mono text-[11px] tabular-nums opacity-60">{tab.count}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="salaries">
          <TxList items={salaries} renderItem={(s) => ({
            title: t("finances.month_salary_title", { month: fmtMonth(s.pay_month) }),
            subtitle: s.paid_at ? t("finances.paid_at", { date: fmtIso(s.paid_at) }) : t("finances.due"),
            meta: s.net_amount ? t("finances.net_amount", { amount: fmtFt(s.net_amount) }) : null,
            amount: fmtFt(s.amount),
            status: s.status,
          })} t={t} emptyMsg={t("finances.no_salary_item")} />
        </TabsContent>
        <TabsContent value="bonuses">
          <TxList items={bonuses} renderItem={(b) => ({
            title: b.reason || t("finances.bonus"),
            subtitle: `${fmtIso(b.issued_at)}${b.paid_at ? ` · ${t("finances.paid_short")}: ${fmtIso(b.paid_at)}` : ""}`,
            meta: b.reject_reason ? t("finances.reject_reason", { reason: b.reject_reason }) : null,
            amount: fmtFt(b.amount),
            status: b.status,
          })} t={t} emptyMsg={t("finances.no_bonus")} />
        </TabsContent>
        <TabsContent value="commissions">
          <TxList items={commissions} renderItem={(c) => ({
            title: c.reason || t("finances.commission"),
            subtitle: `${fmtIso(c.issued_at)}${c.paid_at ? ` · ${t("finances.paid_short")}: ${fmtIso(c.paid_at)}` : ""}`,
            meta: c.reject_reason ? t("finances.reject_reason", { reason: c.reject_reason }) : null,
            amount: fmtFt(c.amount),
            status: c.status,
          })} t={t} emptyMsg={t("finances.no_commission")} />
        </TabsContent>
        <TabsContent value="deductions">
          <TxList items={deductions} renderItem={(d) => ({
            title: d.reason || d.type || t("finances.deduction"),
            subtitle: t("finances.recorded_at", { date: fmtIso(d.created_at) }),
            amount: `− ${fmtFt(d.amount)}`,
            amountDanger: true,
          })} t={t} emptyMsg={t("finances.no_deduction")} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MiniCard({ label, value, pending, icon: Icon, negative }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl bg-zinc-50 p-3.5 dark:bg-zinc-800/40">
      <div className="flex items-center justify-between">
        <p className="text-[12px] text-zinc-500">{label}</p>
        <div className={"grid h-6 w-6 place-items-center rounded-full " + (negative ? "bg-red-50 text-red-500 dark:bg-red-950/30 dark:text-red-400" : "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400")}>
          <Icon className="h-3 w-3" strokeWidth={2.5} />
        </div>
      </div>
      <p className="mt-2 truncate font-mono text-[14.5px] font-semibold tabular-nums tracking-tight">
        {value}
      </p>
      {pending > 0 && (
        <p className="mt-1 truncate text-[10.5px] text-amber-600 dark:text-amber-400">
          {t("finances.plus_pending", { amount: fmtFt(pending) })}
        </p>
      )}
    </div>
  );
}

function TxList({ items, renderItem, emptyMsg, t }) {
  if (items.length === 0) {
    return (
      <div className="rounded-2xl bg-white p-10 text-center shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900">
        <div className="mx-auto grid h-10 w-10 place-items-center rounded-full bg-zinc-100 text-zinc-400 dark:bg-zinc-800">
          <CircleDot className="h-4 w-4" strokeWidth={1.8} />
        </div>
        <p className="mt-3 text-[13.5px] text-zinc-500">{emptyMsg}</p>
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04),0_1px_2px_-1px_rgba(0,0,0,0.02)] dark:bg-zinc-900">
      {items.map((item, idx) => {
        const r = renderItem(item);
        const s = r.status ? statusMeta(r.status, t) : null;
        return (
          <div
            key={item.id}
            data-testid="finance-tx-row"
            className={"flex items-center justify-between gap-4 px-5 py-4 " + (idx > 0 ? "border-t border-zinc-100 dark:border-zinc-800/60" : "")}
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-[14px] font-medium tracking-tight">{r.title}</p>
              {r.subtitle && (
                <p className="mt-0.5 truncate text-[12px] text-zinc-500">
                  {r.subtitle}
                </p>
              )}
              {r.meta && <p className="mt-0.5 text-[11.5px] text-zinc-400">{r.meta}</p>}
              {s && <StatusBadge status={s} />}
            </div>
            <p
              className={
                "shrink-0 font-mono text-[16px] font-semibold tabular-nums " +
                (r.amountDanger ? "text-red-500 dark:text-red-400" : "")
              }
            >
              {r.amount}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    emerald: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
    amber: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
    red: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300",
    blue: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
    zinc: "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400",
  };
  return (
    <span className={"mt-1.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium " + (map[status.color] || map.zinc)}>
      {status.label}
    </span>
  );
}
